/**
 * Global Symbol Aliasing Utility
 * Maps common TradingView-style tickers and shortcuts to FMP canonical tickers.
 */

export const ALIAS_DICTIONARY = {
  // === 🇺🇸 US Markets ===
  'US500': '^GSPC', 'SPX': '^GSPC', 'SP500': '^GSPC', 'INX': '^GSPC',
  'NAS100': '^NDX', 'US100': '^NDX', 'NDX': '^NDX',
  'US30': '^DJI', 'DJI': '^DJI', 'DOW': '^DJI',
  'NASDAQ': '^IXIC', 'COMP': '^IXIC',
  'US2000': '^RUT', 'RUT': '^RUT',
  'NYSE': '^NYA', 'NYA': '^NYA',
  'VIX': '^VIX',
  
  // === 🇨🇦 / 🇲🇽 Americas ===
  'TSX': '^GSPTSE', 'CANADA60': '^GSPTSE',
  'MEXICO': '^MXX', 'MXX': '^MXX',

  // === 🇪🇺 Europe ===
  'UK100': '^FTSE', 'FTSE': '^FTSE',
  'GER40': '^GDAXI', 'DAX': '^GDAXI', 'DE40': '^GDAXI',
  'FRA40': '^FCHI', 'CAC': '^FCHI', 'PX1': '^FCHI',
  'EU50': '^STOXX50E', 'STOXX50': '^STOXX50E',
  'SPAIN35': '^IBEX', 'IBEX': '^IBEX',
  'ITALY40': 'FTSEMIB.MI', 'MIB': 'FTSEMIB.MI',
  'SWISS20': '^SSMI', 'SMI': '^SSMI',
  'NETHERLANDS25': '^AEX', 'AEX': '^AEX',
  'BEL20': '^BFX',

  // === 🌏 Asia / Pacific ===
  'JP225': '^N225', 'NIKKEI': '^N225', 'N225': '^N225',
  'HK50': '^HSI', 'HSI': '^HSI',
  'SHANGHAI': '000001.SS', 'SSEC': '000001.SS',
  'SZSE': '399001.SZ',
  'AUS200': '^AXJO', 'ASX': '^AXJO', 'AXJO': '^AXJO',
  'INDIA50': '^NSEI', 'NIFTY': '^NSEI',
  'SENSEX': '^BSESN',
  'SINGAPORE': '^STI', 'STI': '^STI',
  'TAIWAN': '^TWII', 'TWII': '^TWII',
  'KOREA': '^KS11', 'KOSPI': '^KS11',
  'INDONESIA': '^JKSE', 'JKSE': '^JKSE',
  'MALAYSIA': '^KLSE', 'KLSE': '^KLSE',
  'PHILIPPINES': '^PSEI', 'PSEI': '^PSEI',
  'SET': '^SET.BK', 'THAI': '^SET.BK',

  // === 🌎 South America ===
  'BRAZIL': '^BVSP', 'BVSP': '^BVSP',
  'ARGENTINA': '^MERV', 'MERV': '^MERV',

  // === 🛢️ Commodities ===
  'WTI': 'CLUSD', 'USOIL': 'CLUSD', 'OIL': 'CLUSD',
  'BRENT': 'BZUSD', 'UKOIL': 'BZUSD',
  'NATGAS': 'NGUSD', 'NGAS': 'NGUSD',
  'GOLD': 'XAUUSD', 'XAU': 'XAUUSD',
  'SILVER': 'SIUSD', 'XAG': 'SIUSD',
  'COPPER': 'HGUSD',
  'PLATINUM': 'PLUSD',
  'PALLADIUM': 'PAUSD',
  'CORN': 'ZCUSD',
  'WHEAT': 'ZWUSD',
  'SOYBEAN': 'ZSUSD',
  'SUGAR': 'SBUSD',
  'COFFEE': 'KCUSD',
  'COTTON': 'CTUSD',

  // === 💱 Forex & Other ===
  'DXY': 'DX-Y.NYB', 'USDX': 'DX-Y.NYB',
};

/**
 * Common crypto short codes that should be automatically appended with 'USD'
 * if the user types just the 3-5 letter code.
 */
const CRYPTO_SHORTS = new Set([
  'BTC', 'ETH', 'BNB', 'SOL', 'XRP', 'ADA', 'DOGE', 'DOT', 'MATIC', 'SHIB',
  'LINK', 'AVAX', 'UNI', 'ATOM', 'LTC', 'BCH', 'FIL', 'NEAR', 'APT', 'ARB',
  'OP', 'SUI', 'SEI', 'INJ', 'TIA', 'PEPE', 'WIF', 'BONK', 'RENDER', 'FET',
  'AGIX', 'RNDR', 'GRT', 'IMX', 'SAND', 'MANA', 'AXS', 'AAVE', 'CRV', 'MKR',
  'SNX', 'COMP', 'ALGO', 'VET', 'HBAR', 'EOS', 'XTZ', 'EGLD', 'THETA', 'ICP',
  'FLOW', 'MINA', 'STX', 'ROSE', 'KDA', 'CKB', 'ZEC', 'DASH', 'XMR', 'TON',
  'TRX', 'USDT', 'USDC', 'DAI', 'BUSD', 'TUSD'
]);

/**
 * Resolves a user-inputted symbol to its official canonical FMP equivalent.
 * Applies exact dictionary matching, then crypto auto-appending.
 * @param {string} inputSymbol 
 * @returns {string} 
 */
export const resolveSymbolAlias = (inputSymbol) => {
  if (!inputSymbol || typeof inputSymbol !== 'string') return inputSymbol;
  
  const clean = inputSymbol.trim().toUpperCase();
  
  // 1. Check direct alias mapping
  if (ALIAS_DICTIONARY[clean]) {
    return ALIAS_DICTIONARY[clean];
  }

  // 2. Check Crypto Auto-Append
  if (CRYPTO_SHORTS.has(clean)) {
    return `${clean}USD`;
  }

  // 3. Fallback to original
  return clean;
};

/**
 * Helper to inject aliases into autocomplete database.
 * Returns an object suitable for merging into STOCK_DATABASE.
 */
export const generateDatabaseAliases = () => {
  const dbAliases = {};
  for (const [alias, canonical] of Object.entries(ALIAS_DICTIONARY)) {
    // Only inject if it's a "friendly" name (no carrots/dots) so users can search for it easily
    if (!alias.includes('^') && !alias.includes('.')) {
      // Create a descriptive name for the search bar
      dbAliases[alias] = `${alias} ➔ ${canonical} (Alias)`;
    }
  }
  return dbAliases;
};

/**
 * Reverse mapping for UI Display.
 * Used to hide ugly canonical FMP tickers (e.g. ^GSPC) and show beautiful names (US500).
 */
const PRIMARY_DISPLAY_ALIASES = {
  '^GSPC': 'US500',
  '^NDX': 'US100',
  '^DJI': 'US30',
  '^IXIC': 'NASDAQ',
  '^RUT': 'US2000',
  '^NYA': 'NYSE',
  '^VIX': 'VIX',
  '^FTSE': 'UK100',
  '^GDAXI': 'GER40',
  '^FCHI': 'FRA40',
  '^STOXX50E': 'EU50',
  '^IBEX': 'SPAIN35',
  'FTSEMIB.MI': 'ITALY40',
  '^SSMI': 'SWISS20',
  '^AEX': 'NETHERLANDS25',
  '^N225': 'JP225',
  '^HSI': 'HK50',
  '^AXJO': 'AUS200',
  '^NSEI': 'INDIA50',
  '^SET.BK': 'SET',
  'CLUSD': 'WTI',
  'BZUSD': 'BRENT',
  'NGUSD': 'NATGAS',
  'XAUUSD': 'GOLD',
  'SIUSD': 'SILVER',
  'HGUSD': 'COPPER',
  'DX-Y.NYB': 'DXY',
};

/**
 * Formats a canonical symbol into its primary display alias (if any).
 * @param {string} symbol 
 * @returns {string}
 */
export const formatDisplaySymbol = (symbol) => {
  if (!symbol || typeof symbol !== 'string') return symbol;
  const upper = symbol.toUpperCase();
  return PRIMARY_DISPLAY_ALIASES[upper] || upper;
};
